import { jsPDF } from "jspdf";

// Generates the digitised VQF Form A / Form K confirmation as a single-page PDF from a
// buildVqfFormModel() result. This is the printable artifact for the Sumsub OTP route
// (Art. 63 VQF Regulations — electronic documents printable on request): contracting
// partner first, declaration fields in the prescribed order, confirmation evidence last.
export function generateFormConfirmationPDF(model, options = {}) {
  const firmName = (options.firmName || "").toUpperCase() || "COMPLIANT ID";
  const regLine = options.regLine || "Confirmed electronically via questionnaire + phone OTP (FINMA margin 48)";
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const ml = 18;
  const pw = 210 - ml * 2;
  let y = 18;

  const navy = [30, 45, 90];
  const grey = [107, 114, 128];
  const green = [21, 128, 61];

  const checkPage = (need) => { if (y + need > 278) { doc.addPage(); y = 18; } };
  const wrap = (text, size, width) => doc.setFontSize(size) && doc.splitTextToSize(String(text || ""), width);

  // Header
  doc.setFont("helvetica", "bold"); doc.setFontSize(13); doc.setTextColor(...navy);
  doc.text(firmName, ml, y); y += 5;
  doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(...grey);
  doc.text(regLine, ml, y); y += 8;

  // Full VQF form title
  doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(...navy);
  const titleLines = doc.splitTextToSize(model.title, pw);
  doc.text(titleLines, ml, y); y += titleLines.length * 5 + 4;

  // Contracting partner block — sourced from the verified company record
  const cp = model.contractingPartner || {};
  doc.setFillColor(243, 244, 246); doc.setDrawColor(229, 231, 235);
  const cpLines = [
    ["Contracting partner", cp.legalName],
    ["Registered address", cp.address],
    ["Country", cp.country],
    cp.registrationNumber ? ["Registration number", cp.registrationNumber] : null,
  ].filter(Boolean);
  const cpH = cpLines.length * 6 + 8;
  checkPage(cpH + 4);
  doc.rect(ml, y, pw, cpH, "FD"); y += 6;
  cpLines.forEach(([k, v]) => {
    doc.setFont("helvetica", "bold"); doc.setFontSize(8.5); doc.setTextColor(...grey);
    doc.text(k.toUpperCase(), ml + 4, y);
    doc.setFont("helvetica", "normal"); doc.setFontSize(9.5); doc.setTextColor(17, 24, 39);
    doc.text(String(v || "—"), ml + 52, y);
    y += 6;
  });
  y += 6;

  // Declaration fields in the prescribed order
  (model.fields || []).forEach(({ label, value }) => {
    const valLines = wrap(value, 9.5, pw - 56);
    const rowH = Math.max(6, valLines.length * 4.5 + 2);
    checkPage(rowH + 2);
    doc.setFont("helvetica", "bold"); doc.setFontSize(8.5); doc.setTextColor(...grey);
    const labLines = doc.splitTextToSize(label, 48);
    doc.text(labLines, ml, y);
    doc.setFont("helvetica", "normal"); doc.setFontSize(9.5); doc.setTextColor(17, 24, 39);
    doc.text(valLines, ml + 52, y);
    y += Math.max(labLines.length * 4, valLines.length * 4.5) + 3.5;
    doc.setDrawColor(243, 244, 246); doc.line(ml, y - 2, ml + pw, y - 2);
  });
  y += 3;

  // Confirmation evidence — green box with IP and date
  const c = model.confirmation || {};
  checkPage(30);
  doc.setFillColor(240, 253, 244); doc.setDrawColor(187, 247, 208);
  doc.rect(ml, y, pw, 26, "FD"); y += 6;
  doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...green);
  doc.text("ELECTRONICALLY CONFIRMED", ml + 4, y); y += 5;
  doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.setTextColor(55, 65, 81);
  doc.text(`Signer: ${model.signerName || "—"}   ·   Confirmed: ${c.at ? new Date(c.at).toLocaleString("en-GB") : "—"}   ·   IP: ${c.ip || "—"}`, ml + 4, y); y += 4.5;
  doc.text(`Sumsub applicant action: ${c.actionId || "—"}`, ml + 4, y); y += 4.5;
  doc.text("Confirmation required completion of both the declaration questionnaire and phone OTP verification.", ml + 4, y); y += 10;

  // Footer
  doc.setFontSize(7.5); doc.setTextColor(...grey);
  doc.text(`Generated ${new Date().toLocaleString("en-GB")} · Printable electronic record per Art. 63 VQF Regulations · Authoritative record retained per Art. 7 AMLA`, ml, 290);

  if (options.returnBlob) return doc.output("blob");
  const fname = `Form_${model.formType}_${(model.signerName || "signer").replace(/\s+/g, "_")}.pdf`;
  doc.save(fname);
  return null;
}
